import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Row,
    Col,
    Button,
    Collapse,
    Typography,
    Flex,
} from '@app/ui';
import { PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import AutomatedParam from './AutomatedParam';
import { getParamsOptions, baseParamOf } from './options';

import AlertBanner from '@app/components/common/AlertBanner';
import connect from '@app/utils/connect';
import {
    OperatorToggle,
    ResultingLogicPreview,
    type LogicalOperator,
} from './AndOrLogic';
import {
    buildLabelMaps,
    buildPreviewItems,
    isRealParam,
} from './paramValueDescription';

// Fixed width of the left AND/OR operator rail, so every condition and group shares
// one left column and the compact operator selects line up as a vertical column.
const OP_COL_WIDTH = 62;

// Height of a condition's primary input row (default antd Select), used to vertically
// center the left-rail operator against that row rather than the taller block below it.
const INPUT_ROW_HEIGHT = 32;

type AutomatedParamsContainerProps = {
    values: {
        allSelectedParams: {
            [key: string]: {
                [key: string]: any;
            };
        };
    };
    errors: any;
    setTouched: (values: any) => void;
    setValues: (values: any) => void;
    setErrors: (values: any) => void;
    isEditMode?: boolean;
    activeParams: string[];
    setActiveParams: (values: string[]) => void;
    logicalOperators: Record<string, LogicalOperator>;
    setLogicalOperator: (paramName: string, op: LogicalOperator) => void;
    // SP-8647: group-first builder. Every row belongs to a group (a precedence
    // bracket); each row keeps its own AND/OR joining it to the previous sibling.
    groupAssignments: Record<string, string>;
    // Append a new group seeded with one empty condition.
    onAddGroup: () => void;
    // Append a new empty condition to an existing group.
    onAddCondition: (groupId: string) => void;
    // Remove a whole group (and all its conditions).
    onRemoveGroup: (groupId: string) => void;
    // Carry a row's group + operator across the emptyParam → chosen-param rename.
    onRowActivated: (oldKey: string, newKey: string) => void;
    type: string;
    entityType: string;
    // injected via connect — used to resolve id values to labels in the preview
    filters?: any;
    client?: any;
};

const AutomatedParamsContainer = ({
    values,
    errors,
    setTouched,
    setValues,
    setErrors,
    isEditMode,
    activeParams,
    setActiveParams,
    logicalOperators,
    setLogicalOperator,
    groupAssignments,
    onAddGroup,
    onAddCondition,
    onRemoveGroup,
    onRowActivated,
    type,
    entityType,
    filters,
    client,
}: AutomatedParamsContainerProps) => {
    const { t } = useTranslation(['tools', 'common']);
    const automatedParamsRefs = useRef([]);
    const [focusIndex, setFocusIndex] = useState(null);
    const paramsOptions = getParamsOptions(entityType);

    useEffect(() => {
        automatedParamsRefs.current = automatedParamsRefs.current.slice(
            0,
            activeParams.length,
        );
    }, [activeParams]);

    useEffect(() => {
        if (automatedParamsRefs.current[focusIndex]) {
            // 'nearest': only scroll when the row is actually out of view. Every click
            // inside a row lands here (the row wrapper's onClick), so an unconditional
            // top-align made the screen jump on each value the user picked.
            automatedParamsRefs.current[focusIndex].scrollIntoView({
                block: 'nearest',
            });
            setFocusIndex(null);
        }
    }, [focusIndex]);

    const setSelectedParam = useCallback(
        ({ param, sub }) =>
            selectedValue => {
                if (isEditMode) return;
                let payload = {};
                if (!sub) {
                    payload = {
                        ...values,
                        allSelectedParams: {
                            ...values.allSelectedParams,
                            [param]: selectedValue,
                        },
                    };
                } else {
                    payload = {
                        ...values,
                        allSelectedParams: {
                            ...values.allSelectedParams,
                            [param]: {
                                ...values.allSelectedParams[param],
                                [sub]: selectedValue,
                            },
                        },
                    };
                }

                setValues(payload);
            },
        [values, setValues, isEditMode],
    );

    const onBlur = useCallback(
        param => () => {
            if (isEditMode) return;
            const payload = {
                ...values,
                allSelectedParams: {
                    ...values.allSelectedParams,
                    [param]: true,
                },
            };
            setTouched(payload);
        },
        [values, setTouched, isEditMode],
    );

    const activateParam = useCallback(
        index => param => {
            if (isEditMode) return;
            const oldKey = activeParams[index];
            const newActiveParams = [...activeParams];
            // Replace emptyParam identifier with actual param value
            newActiveParams.splice(index, 1, param);
            setActiveParams(newActiveParams);
            // Carry the row's group membership + operator across the key rename so
            // the newly-chosen condition stays inside its group.
            if (oldKey && oldKey !== param) onRowActivated(oldKey, param);
        },
        [activeParams, setActiveParams, isEditMode, onRowActivated],
    );

    const removeActiveParam = useCallback(
        param => () => {
            if (isEditMode) return;

            // Check if it's an emptyParam identifier or a real param. Resolve via
            // the base param value so duplicate row keys ('sponsors#2') match too.
            const isEmptyParam =
                typeof param === 'string' && param.startsWith('emptyParam');
            const isRealParamRow =
                param &&
                !isEmptyParam &&
                paramsOptions.find(p => p.value == baseParamOf(param));

            const paramType = isRealParamRow
                ? paramsOptions.find(p => p.value == baseParamOf(param))?.type
                : null;

            let payload = { ...values };
            let newActiveParams = [...activeParams];
            let errorPayload = { ...errors };

            const showArray =
                paramType == 'text' || paramType == 'usernamesObject';

            if (isEmptyParam) {
                // Just remove the emptyParam identifier
                newActiveParams = activeParams.filter(ap => ap !== param);
            } else if (isRealParamRow) {
                // Remove the real param and clear its data
                newActiveParams = activeParams.filter(ap => ap !== param);
                payload = {
                    ...values,
                    allSelectedParams: {
                        ...(values.allSelectedParams || {}),
                        [param]: showArray ? [] : {},
                    },
                };
                errorPayload = {
                    ...errors,
                    allSelectedParams: {
                        ...(errors.allSelectedParams || {}),
                        [param]: null,
                    },
                };
            } else {
                // Fallback: remove empty string or unknown values
                newActiveParams = activeParams.filter(
                    ap => ap !== param && ap !== '',
                );
            }

            setActiveParams(newActiveParams);
            setValues(payload);
            setErrors(errorPayload);
        },
        [activeParams, errors, values, isEditMode, paramsOptions],
    );

    const _focus = index => () => {
        setFocusIndex(index);
    };

    // Check for empty rows (either empty string or emptyParam identifiers)
    const hasEmptyRow = activeParams.some(
        ap =>
            ap === '' ||
            (typeof ap === 'string' && ap.startsWith('emptyParam')),
    );
    // SP-8141: parameters may repeat across rows (e.g. two Sponsors conditions),
    // so there's no "used all params" ceiling. A generous cap still guards against
    // runaway rows.
    const MAX_ROWS = 30;
    const disableAdd =
        hasEmptyRow || isEditMode || activeParams.length >= MAX_ROWS;

    const entityName = useMemo(() => {
        const key =
            type === 'collections'
                ? 'tools:collections.title'
                : 'common:filters.campaigns';
        return t(key);
    }, [type, t]);

    // Singular form ("Collection"/"Campaign") for labels that read better in
    // the singular (entityName is the plural "Collections"/"Campaigns").
    const singularEntity = useMemo(() => entityName.slice(0, -1), [entityName]);

    // id→label maps (sponsors, platforms, …) so the preview can show real values.
    const labelMaps = useMemo(
        () => buildLabelMaps({ filters, client, entityType }),
        [filters, client, entityType],
    );

    // Top-level preview items (bare conditions + bracketed groups), in row order,
    // each carrying the operator joining it to the previous item. Drives the
    // read-only "resulting logic" preview.
    const previewItems = useMemo(
        () =>
            buildPreviewItems({
                activeParams,
                allSelectedParams: values.allSelectedParams,
                logicalOperators,
                groupAssignments,
                paramsOptions,
                labelMaps,
            }),
        [
            activeParams,
            values.allSelectedParams,
            logicalOperators,
            groupAssignments,
            paramsOptions,
            labelMaps,
        ],
    );

    // Group-first render model: every row belongs to a group, so each top-level item
    // is a group band holding a contiguous run of same-group rows. A row that somehow
    // lacks an assignment (defensive) renders as its own singleton band. Each member
    // tracks its original activeParams index so refs / activation stay index-based.
    type RenderGroup = {
        groupId: string;
        members: { param: string; index: number }[];
    };
    const renderGroups = useMemo<RenderGroup[]>(() => {
        const out: RenderGroup[] = [];
        activeParams.forEach((param, index) => {
            const groupId = groupAssignments[param] ?? `__solo_${param}`;
            const last = out[out.length - 1];
            if (last && last.groupId === groupId) {
                last.members.push({ param, index });
            } else {
                out.push({ groupId, members: [{ param, index }] });
            }
        });
        return out;
    }, [activeParams, groupAssignments]);

    // Renders the param picker + value editor for one row (no connector/checkbox).
    // `groupSiblings` are the OTHER rows of the same group: their base parameters are
    // disabled in this row's picker, so one group can't hold two conditions of the
    // same type (duplicates stay allowed across groups, per SP-8141).
    const renderParamRow = (
        param: string,
        i: number,
        groupSiblings: { param: string }[],
    ) => {
        const takenByGroup = groupSiblings
            .map(m => m.param)
            .filter(p => p !== param && isRealParam(p))
            .map(baseParamOf);
        const paramError =
            param &&
            !param.startsWith('emptyParam') &&
            errors?.allSelectedParams?.[param]
                ? errors.allSelectedParams[param]
                : null;
        return (
            <div
                ref={el => (automatedParamsRefs.current[i] = el)}
                onClick={_focus(i)}
                style={{ flex: 1 }}
            >
                <AutomatedParam
                    p={param}
                    activeParams={activeParams}
                    setActiveParam={activateParam(i)}
                    removeActiveParam={removeActiveParam}
                    setSelectedParam={setSelectedParam}
                    onBlur={onBlur}
                    allSelectedParams={values.allSelectedParams}
                    disabled={isEditMode}
                    entityType={entityType}
                    error={paramError}
                    disabledParams={takenByGroup}
                />
            </div>
        );
    };

    return (
        <>
            <Row>
                <Col span={24}>
                    <AlertBanner
                        message={t(
                            'tools:automatedParams.messages.chooseFilters',
                            {
                                entity: singularEntity.toLowerCase(),
                            },
                        )}
                        context="info"
                        closable={false}
                    />
                </Col>
            </Row>

            <Collapse
                expandIconPosition="end"
                defaultActiveKey={['1']}
                style={{ marginTop: 24 }}
            >
                <Collapse.Panel
                    style={{ fontWeight: 600 }}
                    header={t('tools:automatedParams.headers.automatedParams')}
                    key="1"
                >
                    {/* Live "resulting logic" summary, back at the TOP of the builder.
                        Two things keep it from flickering the rows below as it grows:
                        it is always rendered (the empty text reserves its one-line
                        footprint from the first interaction, so there's no pop-in),
                        and AnimatedHeight turns each height change into one short
                        glide — then caps the content with an inner scroll, so past a
                        few lines the card stops moving the builder altogether. */}
                    <div
                        style={{
                            marginBottom: 16,
                            padding: '12px 16px',
                            background: '#fff',
                            border: '1px solid #BFBFBF',
                            borderRadius: 8,
                        }}
                        data-test="resulting-logic-bar"
                    >
                        <ResultingLogicPreview
                            conditions={[]}
                            items={previewItems}
                            title={t(
                                'tools:automatedParams.headers.resultingLogic',
                                { entity: singularEntity },
                            )}
                            emptyText={t(
                                'tools:automatedParams.messages.noConditionsYet',
                                { defaultValue: 'No conditions yet' },
                            )}
                            maxContentHeight={168}
                        />
                    </div>

                    {/* Group-first builder: every condition lives in a group. A
                        left-rail AND/OR toggle joins each condition (2nd onward) to the
                        one above it — and each group to the group above it — so the
                        operators read as a vertical "tennis-score" column the user can
                        flip in place. "Add condition" lives inside each group; "Add
                        group" sits below all of them. No grouping checkbox. */}
                    {renderGroups.map((group, gIdx) => {
                        // The operator joining THIS group to the previous one is stored
                        // on the group's first row (matches the serializer's top-level
                        // join). Shown from the 2nd group onward.
                        const firstParam = group.members[0].param;

                        return (
                            <React.Fragment key={`grp_${group.groupId}`}>
                                {gIdx > 0 && (
                                    <div
                                        style={{
                                            width: OP_COL_WIDTH,
                                            marginTop: 12,
                                        }}
                                        data-test="group-connector"
                                    >
                                        <OperatorToggle
                                            value={
                                                logicalOperators[firstParam] ??
                                                'AND'
                                            }
                                            onChange={op =>
                                                setLogicalOperator(
                                                    firstParam,
                                                    op,
                                                )
                                            }
                                            disabled={isEditMode}
                                        />
                                    </div>
                                )}
                                <div
                                    style={{
                                        // App standard border token (colorBorder-
                                        // Secondary), matching inputs + the Collapse
                                        // panel; the faint #e4e9f0 read as no border.
                                        border: '1px solid #BFBFBF',
                                        borderRadius: 8,
                                        padding: '8px 12px 12px',
                                        marginTop: 12,
                                        background: '#fbfcfe',
                                    }}
                                    data-test="group-band"
                                >
                                    <Flex
                                        align="center"
                                        gap={8}
                                        style={{ flexWrap: 'wrap' }}
                                    >
                                        <Typography.Text
                                            type="secondary"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {t(
                                                'tools:automatedParams.headers.group',
                                                { defaultValue: 'Group' },
                                            )}
                                        </Typography.Text>
                                        {!isEditMode && (
                                            <Button
                                                size="small"
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() =>
                                                    onRemoveGroup(group.groupId)
                                                }
                                                style={{
                                                    marginLeft: 'auto',
                                                    // App theme paints button text
                                                    // white; force the standard text
                                                    // color so the label stays legible.
                                                    color: '#515252',
                                                }}
                                                data-test="remove-group-btn"
                                            >
                                                {t(
                                                    'tools:automatedParams.buttons.removeGroup',
                                                    {
                                                        defaultValue:
                                                            'Remove group',
                                                    },
                                                )}
                                            </Button>
                                        )}
                                    </Flex>

                                    {group.members.map((m, mIdx) => (
                                        <Flex
                                            key={`gm_${m.param}`}
                                            align="flex-start"
                                            gap={4}
                                            style={{ marginTop: 8 }}
                                        >
                                            {/* Left operator rail: empty spacer on the
                                                first row so all conditions share one
                                                left column. The box is the input row's
                                                height (INPUT_ROW_HEIGHT) with the select
                                                centered inside it, so the operator lines
                                                up with the condition's first input row —
                                                not the taller block (which also holds a
                                                divider + any wrapped value tags). */}
                                            <div
                                                style={{
                                                    width: OP_COL_WIDTH,
                                                    flexShrink: 0,
                                                    height: INPUT_ROW_HEIGHT,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {mIdx > 0 && (
                                                    <OperatorToggle
                                                        value={
                                                            logicalOperators[
                                                                m.param
                                                            ] ?? 'AND'
                                                        }
                                                        onChange={op =>
                                                            setLogicalOperator(
                                                                m.param,
                                                                op,
                                                            )
                                                        }
                                                        disabled={isEditMode}
                                                    />
                                                )}
                                            </div>
                                            {renderParamRow(
                                                m.param,
                                                m.index,
                                                group.members,
                                            )}
                                        </Flex>
                                    ))}

                                    {!isEditMode && (
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<PlusCircleOutlined />}
                                            onClick={() =>
                                                onAddCondition(group.groupId)
                                            }
                                            disabled={disableAdd}
                                            style={
                                                disableAdd
                                                    ? { marginTop: 8 }
                                                    : {
                                                          marginTop: 8,
                                                          color: 'var(--main-brand-color)',
                                                      }
                                            }
                                            data-test="add-condition-btn"
                                        >
                                            {t(
                                                'tools:automatedParams.buttons.addCondition',
                                                {
                                                    defaultValue:
                                                        'Add condition',
                                                },
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}

                    {!isEditMode && (
                        <Button
                            type="text"
                            icon={<PlusCircleOutlined size={14} />}
                            onClick={onAddGroup}
                            disabled={disableAdd}
                            size="small"
                            style={
                                disableAdd
                                    ? { marginTop: 16 }
                                    : {
                                          marginTop: 16,
                                          color: 'var(--main-brand-color)',
                                      }
                            }
                            data-test="add-group-btn"
                        >
                            {t('tools:automatedParams.buttons.addGroup', {
                                defaultValue: 'Add group',
                            })}
                        </Button>
                    )}
                </Collapse.Panel>
            </Collapse>
        </>
    );
};

export default connect(['filters', 'client'])(AutomatedParamsContainer);
